import React, { useState } from "react";
import { toast } from "sonner";
import { X, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { TRACKING_STEPS, STEP_LABELS } from "../lib/tracking";
import { Button } from "./ui/button";

export default function TrackingDialog({ order, onClose, onChanged }) {
  const { lang } = useLang();
  const labels = STEP_LABELS[lang] || STEP_LABELS.en;
  const [events, setEvents] = useState(order.events || []);
  const [step, setStep] = useState("preparing");
  const [location, setLocation] = useState("");
  const [note, setNote] = useState("");
  const [when, setWhen] = useState(() => {
    const d = new Date();
    d.setSeconds(0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    const { data } = await api.get(`/admin/orders/${order.id}`);
    setEvents(data.events || []);
    onChanged?.(data);
  };

  const add = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/orders/${order.id}/tracking`, {
        step,
        location: location.trim(),
        note: note.trim(),
        occurred_at: new Date(when).toISOString(),
      });
      toast.success("Tracking update added");
      setLocation("");
      setNote("");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const del = async (eid) => {
    if (eid === "seed") return;
    if (!window.confirm("Delete this tracking event?")) return;
    try {
      await api.delete(`/admin/orders/${order.id}/tracking/${eid}`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="tracking-dialog"
    >
      <div
        className="bg-white rounded-md border border-[#E5E1D8] w-full max-w-2xl max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-[#E5E1D8]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#7B5CBF]">Tracking</div>
            <div className="font-mono text-sm mt-1">{order.order_number}</div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[#F3F0EB] rounded"
            data-testid="tracking-dialog-close"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-5 border-b border-[#E5E1D8] space-y-3">
          <div className="text-sm font-medium">Add tracking update</div>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs">
              <div className="text-[#767676] mb-1">Step</div>
              <select
                value={step}
                onChange={(e) => setStep(e.target.value)}
                data-testid="tracking-step-select"
                className="w-full border border-[#E5E1D8] rounded px-2 py-2 text-sm bg-white"
              >
                {TRACKING_STEPS.map((s) => (
                  <option key={s} value={s}>{labels[s]}</option>
                ))}
                <option value="cancelled">{labels.cancelled}</option>
              </select>
            </label>
            <label className="text-xs">
              <div className="text-[#767676] mb-1">When</div>
              <input
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                data-testid="tracking-when"
                className="w-full border border-[#E5E1D8] rounded px-2 py-2 text-sm bg-white"
              />
            </label>
            <label className="text-xs col-span-2">
              <div className="text-[#767676] mb-1">Location (optional)</div>
              <input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. Dubai sorting facility"
                data-testid="tracking-location"
                className="w-full border border-[#E5E1D8] rounded px-2 py-2 text-sm bg-white"
              />
            </label>
            <label className="text-xs col-span-2">
              <div className="text-[#767676] mb-1">Note (optional)</div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                data-testid="tracking-note"
                className="w-full border border-[#E5E1D8] rounded px-2 py-2 text-sm bg-white"
              />
            </label>
          </div>
          <Button
            onClick={add}
            disabled={saving}
            data-testid="tracking-add-btn"
            className="rounded-full bg-[#1A1738] hover:bg-[#2C2650] text-white h-9 px-5 text-xs uppercase tracking-widest"
          >
            {saving ? "Adding…" : "Add update"}
          </Button>
        </div>

        <div className="p-5">
          <div className="text-sm font-medium mb-3">Timeline</div>
          {events.length === 0 ? (
            <div className="text-sm text-[#767676]">No updates yet.</div>
          ) : (
            <ol className="space-y-3">
              {events.map((e) => (
                <li
                  key={e.id}
                  className="flex items-start justify-between gap-3 border border-[#E5E1D8] rounded p-3"
                  data-testid={`tracking-event-${e.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{labels[e.step] || e.step}</div>
                    <div className="text-xs text-[#767676]">{new Date(e.occurred_at).toLocaleString()}</div>
                    {e.location && <div className="text-xs mt-0.5">{e.location}</div>}
                    {e.note && <div className="text-xs text-[#767676] mt-1">{e.note}</div>}
                  </div>
                  {e.id !== "seed" && (
                    <button
                      onClick={() => del(e.id)}
                      className="p-1 text-[#767676] hover:text-[#7B5CBF]"
                      data-testid={`tracking-del-${e.id}`}
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
